import React, { useEffect, useMemo, useRef, useState } from 'react';
import '@/App.css';
import { Toaster } from 'sonner';
import { SEGMENTS } from '@/lib/segments';
import { useScrollProgress } from '@/lib/useScrollProgress';
import StickySkullStage from '@/components/HologramStage';
import NeuronBackground from '@/components/NeuronBackground';
import ChapterRail from '@/components/ChapterRail';
import ScrollProgressBar from '@/components/ScrollProgressBar';
import SegmentRenderer from '@/components/SegmentRenderer';
import PreloaderTitleReveal from '@/components/PreloaderTitleReveal';

export default function App() {
  const [entered, setEntered] = useState(false);
  const storyRef = useRef(null);
  const segmentRefs = useRef([]);
  const { progressRef, activeSegment } = useScrollProgress(storyRef);

  // Build target keyframes once
  const segmentTargets = useMemo(() => SEGMENTS.map((s) => s.target), []);

  useEffect(() => {
    // Lock scroll while preloader is up
    if (!entered) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
    };
  }, [entered]);

  const onJump = (i) => {
    const el = segmentRefs.current[i];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="App relative">
      {/* Global cinematic overlays */}
      <div className="bg-vignette" />
      <div className="bg-noise" />

      {/* Subliminal neural network — represents the bullet's pressure on
          Pandi's neurons. Kept at very low opacity so it never distracts
          from the text, only adds atmosphere. Mounted after entering so the
          preloader stays minimal. */}
      {entered && <NeuronBackground opacity={0.28} />}

      {/* Sticky 3D holographic skull (only after entering, for cleaner load) */}
      {entered && (
        <StickySkullStage progressRef={progressRef} segmentTargets={segmentTargets} />
      )}

      {/* Top progress bar */}
      {entered && <ScrollProgressBar progressRef={progressRef} />}

      {/* Chapter rail */}
      {entered && (
        <ChapterRail segments={SEGMENTS} activeIndex={activeSegment} onJump={onJump} />
      )}

      {/* Header brand strip */}
      {entered && (
        <header className="fixed top-0 left-0 right-0 z-40 pointer-events-none">
          <div className="mx-auto max-w-[1240px] px-5 sm:px-8 py-5 flex items-center justify-between">
            <div className="pointer-events-auto flex items-center gap-3">
              <div className="h-7 w-7 rounded-full border border-[color:var(--gold)]/70 flex items-center justify-center">
                <div className="h-2.5 w-2.5 rounded-full" style={{ background: 'var(--gold)' }} />
              </div>
              <div className="font-deco tracking-[0.18em] text-sm sm:text-base gold-foil">
                BLACK AXE
              </div>
            </div>
            <div className="hidden sm:flex pointer-events-auto items-center gap-4 font-ui text-[10px] tracking-[0.32em] uppercase text-[color:var(--text-dim)]">
              <span>PITCH DECK</span>
              <span className="h-px w-6 bg-[color:var(--gold)]/60" />
              <span className="text-[color:var(--gold)]">v1.0</span>
            </div>
          </div>
        </header>
      )}

      {/* Story rail */}
      <main ref={storyRef} className="relative z-20">
        {SEGMENTS.map((s, i) => (
          <SegmentRenderer
            key={s.id}
            data={s}
            index={i}
            segmentRef={(el) => (segmentRefs.current[i] = el)}
          />
        ))}
      </main>

      {/* Preloader / title reveal */}
      {!entered && <PreloaderTitleReveal onEnter={() => setEntered(true)} />}

      <Toaster
        position="bottom-center"
        theme="dark"
        toastOptions={{
          style: {
            background: '#141516',
            color: '#F3EBDD',
            border: '1px solid rgba(200,162,74,0.35)',
            fontFamily: 'Inter, system-ui, sans-serif',
          },
        }}
      />
    </div>
  );
}
